import { useState, useEffect } from 'react';
import * as userService from './../services/userService';
import * as userAuthService from './../services/userAuthService';
import * as crmActivityService from './../services/crmActivityService';
import * as crmCommService from './../services/crmCommService';
import * as crmInstituteService from './../services/crmInstituteService';
import * as crmLeadsService from './../services/crmLeadsService';
import * as crmOpportunityService from './../services/crmOpportunityService';
import * as documentService from './../services/documentService';
import * as addressService from './../services/addressService';
import * as inventoryAllocService from './../services/inventoryAllocService';
import * as inventoryStockService from './../services/inventoryStockService';
import * as inventoryTransactionService from './../services/inventoyrTransactionService';
import * as salesOrdersService from './../services/salesOrders';
import * as salesPaymentsService from './../services/salesPayments';
import * as salesPlanService from './../services/salesPlan';
import * as salesQuotesService from './../services/salesQuotesService';
import * as salesServiceService from './../services/salesServiceService';
import * as salesSubscriptionService from './../services/salesSubscriptionService';
import * as saleContactsService from './../services/saleContactsService';
import * as salesInstitutesService from './../services/salesInstitutesService';
import * as taxService from './../services/taxService';
import * as crmContactService from './../services/crmContactService';
import * as discountService from './../services/discountService';
import * as productService from './../services/inventoryProductsService';
import * as salesProductService from './../services/salesProductService';

import * as marketCampaignService from 'services/marketCampaignService';

const useFetchData = <T>(
  fetchFunction: (...args: any[]) => Promise<T[]>,
  ...args: any[]
) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchFunction(...args);
        setData(response);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchFunction, ...args]);

  return { data, loading };
};

const createHook =
  (fetchFunction: (...args: any[]) => Promise<any[]>) =>
  (...args: any[]) =>
    useFetchData(fetchFunction, ...args);

// Export hooks dynamically
const apiHooks = {
  useUsers: createHook(userService.fetchUsers),
  useFetchCrmActivitiesByTypeById: (
    related_to_type: string,
    related_to_id: string
  ) =>
    useFetchData(() =>
      crmActivityService.fetchCrmActivitiesByTypeById(
        related_to_type,
        related_to_id
      )
    ),
  useLeadDetails: (leadId: string) =>
    useFetchData(() => crmLeadsService.fetchCrmLeadsById(leadId)),
  useCrmContact: createHook(crmContactService.fetchCrmContact),
  useUserAuth: createHook(userAuthService.fetchUser),
  useCRMActivities: createHook(crmActivityService.fetchCrmActivities),
  useCRMActivitiesByTypeId: (related_to_type: string, related_to_id: string) =>
    useFetchData(() =>
      crmActivityService.fetchCrmActivitiesByTypeById(
        related_to_type,
        related_to_id
      )
    ),
  useCRMCommunications: createHook(crmCommService.fetchCrmCommunications),
  useCRMInstitutes: createHook(crmInstituteService.fetchCrmInstitutes),
  useCRMLeads: createHook(crmLeadsService.fetchCrmLeads),
  useConvertLeadToOpportunity: (leadId: any) =>
    useFetchData(() => crmLeadsService.convertLeadToOpportunity(leadId)),
  useCRMOpportunitiesById: (leadId: any) =>
    useFetchData(() => crmOpportunityService.fetchCrmOpportunitiesById(leadId)),
  useCRMOpportunities: createHook(crmOpportunityService.fetchCrmOpportunities),
  useDocuments: createHook(documentService.fetchDocuments),
  useAddresses: createHook(addressService.fetchAddresses),
  useInventoryAllocations: createHook(
    inventoryAllocService.fetchInventoryAllocations
  ),
  useInventoryProducts: createHook(productService.fetchInventoryProducts),
  useCategoryHook: createHook(salesProductService.fetchSalesCategory),
  useInventoryStock: createHook(inventoryStockService.fetchInventoryStock),
  useInventoryTransactions: createHook(
    inventoryTransactionService.fetchInventoryTransactions
  ),
  useSalesOrders: createHook(salesOrdersService.fetchSalesOrders),
  useSalesPayments: createHook(salesPaymentsService.fetchSalesPayments),
  useSalesPlans: createHook(salesPlanService.fetchSalesPlans),
  useSalesQuotes: createHook(salesQuotesService.fetchSalesQuotes),
  useSalesServices: createHook(salesServiceService.fetchSalesServices),
  useSalesServicesById: (id: any) =>
    useFetchData(() => salesServiceService.fetchSalesServiceById(id)),
  useSalesSubscriptions: createHook(
    salesSubscriptionService.fetchSubscriptions
  ),
  useSalesContacts: createHook(saleContactsService.fetchSalesContacts),
  useSalesInstitutes: createHook(salesInstitutesService.fetchSalesInstitutes),
  useTaxes: createHook(taxService.fetchTaxes),
  useDiscounts: createHook(discountService.fetchDiscounts),
  useCampaigns: createHook(marketCampaignService.getMarketCampaigns)
};

export default apiHooks;
